import React, { useMemo } from "react";

import type { ColDef, ColGroupDef, GridApi } from "ag-grid-community";

import AgGrid from "components/AgGrid/AgGrid";
import SavedViewsPanel from "components/AgGrid/panels/SavedViewsPanel";

import { createDatasource } from "./datasource";

interface DataGridProps {
  datasetId: string;
  roleSlug: string;
  scope: string;
  columnDefs: (ColDef | ColGroupDef)[];
  onGridReady?: (api: GridApi) => void;
}

const DEFAULT_COL: ColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 80,
};

export function DataGrid({
  datasetId,
  roleSlug,
  scope,
  columnDefs,
  onGridReady,
}: DataGridProps) {
  const tableName = `dataexplorer_${datasetId}`;

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
        {
          id: "savedViews",
          labelDefault: "Views",
          labelKey: "savedViews",
          iconKey: "save",
          toolPanel: SavedViewsPanel,
          toolPanelParams: { tableName },
        },
      ],
    }),
    [tableName],
  );

  const datasource = useMemo(
    () => createDatasource(datasetId, roleSlug, scope),
    [datasetId, roleSlug, scope],
  );

  return (
    // key forces a full remount when dataset or scope changes, which clears all grid state
    // (sorts, filters, grouping) and prevents stale column state carrying across datasets.
    // cacheBlockSize must match paginationPageSize so each page is exactly one server fetch.
    <AgGrid
      key={`${datasetId}-${scope}`}
      columnDefs={columnDefs}
      defaultColDef={DEFAULT_COL}
      rowModelType="serverSide"
      serverSideDatasource={datasource}
      cacheBlockSize={100}
      pagination
      paginationPageSize={100}
      rowGroupPanelShow="always"
      sideBar={sideBar}
      cellSelection
      enableCharts
      popupParent={document.body}
      groupDisplayType="multipleColumns"
      autoGroupColumnDef={{ minWidth: 200, chartDataType: "category" }}
      onGridReady={onGridReady ? e => onGridReady(e.api) : undefined}
    />
  );
}
